import type {
  SmartCreationReviewAdGroup,
  SmartCreationReviewCampaign,
  SmartCreationReviewEditChange,
  SmartCreationReviewWorkspace,
} from "@adpilot/shared";
import {
  IconChevronDown,
  IconChevronRight,
  IconCornerDownRight,
  IconSearch,
} from "@tabler/icons-react";
import type { ReactNode } from "react";
import { Fragment, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import type { ReviewChangeHandler } from "./review-change-handler";

interface ReviewCampaignTableProps {
  workspace: SmartCreationReviewWorkspace;
  planId?: string;
  query: string;
  disabled: boolean;
  onApply: ReviewChangeHandler;
  onApplyOptimistic?: ReviewChangeHandler;
  onInspect(campaign: SmartCreationReviewCampaign, trigger: HTMLElement): void;
  onTargets(group: SmartCreationReviewAdGroup): void;
  selectedCampaignKeys?: ReadonlySet<string>;
  onQueryChange?(query: string): void;
  actions?: ReactNode;
}

/** Campaigns disclose the canonical graph; field editing belongs to their detail panels. */
export function ReviewCampaignTable({
  workspace,
  query,
  disabled: externalDisabled,
  onApply,
  onApplyOptimistic,
  onInspect,
  onTargets,
  selectedCampaignKeys,
  onQueryChange,
  actions,
}: ReviewCampaignTableProps) {
  const { t, i18n } = useTranslation("smartCreation");
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [localQuery, setLocalQuery] = useState(query);
  const [pending, setPending] = useState(false);
  const mutationLock = useRef(false);
  const disabled = externalDisabled || pending;
  const readOnly = workspace.status === "confirmed";
  const canView = readOnly || !externalDisabled;
  useEffect(() => setLocalQuery(query), [query]);
  const search = localQuery.trim().toLocaleLowerCase();
  const visible = workspace.graph.campaigns.filter((campaign) => {
    const groups = workspace.graph.adGroups.filter(
      (group) => group.campaignLineageKey === campaign.ref.lineageKey,
    );
    const targets = workspace.graph.targets.filter((target) =>
      campaign.adGroupLineageKeys.includes(target.adGroupLineageKey),
    );
    return (
      !search ||
      [
        campaign.name.effective,
        ...groups.map((group) => group.name.effective),
        ...targets.map((target) =>
          target.type === "keyword"
            ? target.keyword
            : target.type === "product"
              ? target.asin
              : target.type === "category"
                ? target.categoryId
                : target.type === "category_brand"
                  ? `${target.categoryId} ${target.brandId}`
                  : target.expressionType,
        ),
      ]
        .join(" ")
        .toLocaleLowerCase()
        .includes(search)
    );
  });
  const currency = (amount: number) =>
    new Intl.NumberFormat(i18n.resolvedLanguage, {
      style: "currency",
      currency: workspace.summary.currencyCode,
    }).format(amount);
  const intention = (groupKeys: readonly string[]) =>
    [
      ...new Set(
        workspace.graph.targets
          .filter((target) => groupKeys.includes(target.adGroupLineageKey))
          .map((target) => t(`strategy.${target.strategyKind}`)),
      ),
    ].join(" / ") || "—";
  async function apply(changes: SmartCreationReviewEditChange[]) {
    if (externalDisabled || !changes.length) return false;
    // Inclusion commands are queued by the owner; they must remain interactive
    // while earlier optimistic choices are being saved.
    if (
      onApplyOptimistic &&
      changes.every((change) => change.operation === "set_campaign_excluded")
    ) {
      try {
        return (await onApplyOptimistic(changes)) !== false;
      } catch {
        toast.error(t("review.parity.applyFailed"));
        return false;
      }
    }
    if (mutationLock.current) return false;
    mutationLock.current = true;
    setPending(true);
    try {
      return (await onApply(changes)) !== false;
    } catch {
      toast.error(t("review.parity.applyFailed"));
      return false;
    } finally {
      mutationLock.current = false;
      setPending(false);
    }
  }
  return (
    <div className="flex min-h-0 flex-col" data-review-campaign-table>
      <div
        className="flex min-h-16 flex-wrap items-center justify-between gap-2 py-2"
        data-review-table-toolbar
      >
        <InputGroup className="w-[272px] max-w-full">
          <InputGroupAddon>
            <IconSearch className="size-3.5" />
          </InputGroupAddon>
          <InputGroupInput
            size="sm"
            value={localQuery}
            aria-label={t("review.preview.search")}
            placeholder={t("review.preview.search")}
            onChange={(event) => {
              setLocalQuery(event.target.value);
              onQueryChange?.(event.target.value);
            }}
          />
        </InputGroup>
        {actions}
      </div>
      <section
        className="overflow-hidden rounded-[10px] border border-line bg-surface"
        data-draft-projection="table"
      >
        <div className="max-h-[min(52dvh,520px)] overflow-auto">
          <Table className="min-w-[1080px] table-fixed text-xs [&>thead>tr]:h-10 [&>thead>tr>th]:sticky [&>thead>tr>th]:top-0 [&>thead>tr>th]:z-10 [&>thead>tr>th]:bg-subtle [&>thead>tr>th]:px-1.5 [&>thead>tr>th]:font-normal [&>thead>tr>th]:tracking-normal [&>tbody>tr>td]:px-1.5 [&>tbody>tr>td]:py-0">
            <CampaignColumns />
            <TableHeader>
              <TableRow>
                <TableHead aria-label={t("review.preview.expand")} />
                <TableHead>{t("review.parity.campaign")}</TableHead>
                <TableHead>{t("review.preview.deliveryType")}</TableHead>
                <TableHead>{t("review.parity.intention")}</TableHead>
                <TableHead className="text-right">{t("review.parity.adGroup")}</TableHead>
                <TableHead className="text-right">{t("review.preview.targets")}</TableHead>
                <TableHead className="text-right">{t("review.parity.dailyBudget")}</TableHead>
                <TableHead>{t("review.parity.bidding")}</TableHead>
                <TableHead className="text-center">{t("review.preview.use")}</TableHead>
                <TableHead aria-label={t("review.preview.details")} />
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((campaign) => {
                const groups = workspace.graph.adGroups.filter(
                  (group) => group.campaignLineageKey === campaign.ref.lineageKey,
                );
                const targets = workspace.graph.targets.filter((target) =>
                  campaign.adGroupLineageKeys.includes(target.adGroupLineageKey),
                );
                const open = expanded.has(campaign.ref.lineageKey);
                return (
                  <Fragment key={campaign.ref.lineageKey}>
                    <TableRow
                      className="h-14"
                      data-review-campaign-row={campaign.ref.lineageKey}
                      aria-selected={
                        selectedCampaignKeys?.has(campaign.ref.lineageKey) || undefined
                      }
                    >
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon-xs"
                          aria-label={t("review.preview.expandCampaign", {
                            name: campaign.name.effective,
                          })}
                          aria-expanded={open}
                          aria-controls={`review-groups-${campaign.ref.lineageKey}`}
                          onClick={() =>
                            setExpanded((current) => {
                              const next = new Set(current);
                              if (open) next.delete(campaign.ref.lineageKey);
                              else next.add(campaign.ref.lineageKey);
                              return next;
                            })
                          }
                        >
                          {open ? <IconChevronDown /> : <IconChevronRight />}
                        </Button>
                      </TableCell>
                      <TableCell>
                        <span
                          className="block truncate text-[13px] leading-[19px]"
                          title={campaign.name.effective}
                        >
                          {campaign.name.effective}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1 py-2">
                          <Badge variant="neutral">
                            {targets.some((target) => target.type === "automatic")
                              ? t("review.preview.automaticDelivery")
                              : t("review.preview.manualDelivery")}
                          </Badge>
                          <MatchingTypes
                            targets={targets.filter((target) => !target.excluded.effective)}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <span
                          className="block truncate text-[11px] text-muted-foreground"
                          title={intention(campaign.adGroupLineageKeys)}
                        >
                          {intention(campaign.adGroupLineageKeys)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">{groups.length}</TableCell>
                      <TableCell className="text-right">
                        {targets.filter((target) => !target.excluded.effective).length}
                      </TableCell>
                      <TableCell className="text-right">
                        {campaign.excluded.effective ? (
                          <span className="text-[11px] text-muted-foreground">
                            {t("review.parity.excludedBudget")}
                          </span>
                        ) : (
                          currency(campaign.dailyBudget.effective)
                        )}
                      </TableCell>
                      <TableCell>
                        <span
                          className="block truncate"
                          title={t(`review.bidding.${campaign.biddingStrategy.effective}`)}
                        >
                          {t(`review.bidding.${campaign.biddingStrategy.effective}`)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-center">
                          <Switch
                            checked={!campaign.excluded.effective}
                            disabled={disabled || readOnly}
                            aria-label={t("review.parity.includeCampaign", {
                              name: campaign.name.effective,
                            })}
                            onCheckedChange={(value) =>
                              void apply([
                                {
                                  operation: "set_campaign_excluded",
                                  ref: campaign.ref,
                                  excluded: !value,
                                },
                              ])
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={!canView || pending}
                          aria-label={t("review.parity.editCampaign", {
                            name: campaign.name.effective,
                          })}
                          data-review-trigger-key={campaign.ref.lineageKey}
                          data-review-trigger-kind="row"
                          onClick={(event) => onInspect(campaign, event.currentTarget)}
                        >
                          {t("common:actions.edit")}
                        </Button>
                      </TableCell>
                    </TableRow>
                    {open && (
                      <TableRow
                        id={`review-groups-${campaign.ref.lineageKey}`}
                        className="hover:bg-table-footer"
                      >
                        <TableCell colSpan={10} className="!p-0">
                          <Table className="min-w-full table-fixed bg-table-footer text-xs [&_th]:h-7 [&_th]:bg-table-footer [&_th]:px-1.5 [&_th]:font-normal [&_th]:tracking-normal [&_td]:px-1.5 [&_td]:py-0">
                            <CampaignColumns />
                            <TableHeader>
                              <TableRow className="h-7">
                                <TableHead />
                                <TableHead colSpan={2}>
                                  {t("review.preview.groupCount", { count: groups.length })}
                                </TableHead>
                                <TableHead aria-label={t("review.parity.intention")} />
                                <TableHead />
                                <TableHead aria-label={t("review.preview.targets")} />
                                <TableHead colSpan={3} />
                                <TableHead aria-label={t("review.preview.details")} />
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {groups.map((group) => (
                                <TableRow
                                  key={group.ref.lineageKey}
                                  className="h-12"
                                  data-review-group-row={group.ref.lineageKey}
                                >
                                  <TableCell>
                                    <div className="flex justify-center">
                                      <IconCornerDownRight
                                        className="h-[18px] w-3 text-line"
                                        stroke={1.8}
                                        aria-hidden
                                      />
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex min-w-0 flex-col gap-0.5">
                                      <span
                                        className="truncate font-medium leading-5"
                                        title={group.name.effective}
                                      >
                                        {group.name.effective}
                                      </span>
                                      <span
                                        className="truncate text-[11px] leading-[18px] text-muted-foreground"
                                        title={group.products
                                          .map((product) => `${product.asin} · ${product.sku}`)
                                          .join(" / ")}
                                      >
                                        {group.products.map((product) => product.asin).join(" · ")}{" "}
                                        ·{" "}
                                        {t("review.preview.productAds", {
                                          count: group.products.length,
                                        })}{" "}
                                        · {t("review.preview.followsCampaign")}
                                      </span>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <MatchingTypes
                                      targets={workspace.graph.targets.filter(
                                        (target) =>
                                          target.adGroupLineageKey === group.ref.lineageKey &&
                                          !target.excluded.effective,
                                      )}
                                    />
                                  </TableCell>
                                  <TableCell>
                                    <span className="block truncate text-[11px] text-muted-foreground">
                                      {intention([group.ref.lineageKey])}
                                    </span>
                                  </TableCell>
                                  <TableCell />
                                  <TableCell className="text-right">
                                    <Button
                                      variant="link"
                                      size="xs"
                                      disabled={!canView || pending}
                                      onClick={() => onTargets(group)}
                                      aria-label={t("review.targetSelection.editGroupTargets", {
                                        name: group.name.effective,
                                      })}
                                    >
                                      {t("review.preview.targetCount", {
                                        count: workspace.graph.targets.filter(
                                          (target) =>
                                            target.adGroupLineageKey === group.ref.lineageKey &&
                                            !target.excluded.effective,
                                        ).length,
                                      })}
                                    </Button>
                                  </TableCell>
                                  <TableCell colSpan={3} />
                                  <TableCell>
                                    <Button
                                      variant="ghost"
                                      size="icon-xs"
                                      aria-label={t("review.preview.groupDetails", {
                                        name: group.name.effective,
                                      })}
                                      disabled={!canView || pending}
                                      onClick={() => onTargets(group)}
                                    >
                                      <IconChevronRight />
                                    </Button>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })}
              {!visible.length && (
                <TableRow>
                  <TableCell colSpan={10} className="h-20 text-center text-muted-foreground">
                    {t("review.preview.noResults")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        <div className="flex h-9 items-center justify-between gap-3 border-t border-line bg-table-footer px-4 text-[11px] text-muted-foreground">
          <span>
            {t("review.preview.creationCounts", {
              count: workspace.summary.includedCampaignCount,
            })}
          </span>
        </div>
      </section>
    </div>
  );
}

// The nested table spans the parent width, so sharing all ten columns keeps
// intent, target count, and the detail arrow on the same vertical axes.
function CampaignColumns() {
  return (
    <colgroup>
      <col className="w-9" />
      <col />
      <col className="w-[144px]" />
      <col className="w-[110px]" />
      <col className="w-[68px]" />
      <col className="w-[68px]" />
      <col className="w-32" />
      <col className="w-[166px]" />
      <col className="w-[92px]" />
      <col className="w-[76px]" />
    </colgroup>
  );
}

function MatchingTypes({ targets }: { targets: SmartCreationReviewWorkspace["graph"]["targets"] }) {
  const { t } = useTranslation("smartCreation");
  const labels = [
    ...new Set(
      targets.map((target) =>
        target.type === "automatic"
          ? t(`review.preview.graph.expressions.${target.expressionType}`)
          : target.type === "keyword"
            ? t(`review.match.${target.matchType.effective}`)
            : target.type === "product"
              ? t(`review.match.${target.matchType ?? "exact"}`)
              : t(`review.targetType.${target.type}`),
      ),
    ),
  ];
  return (
    <div className="flex flex-wrap gap-x-2 gap-y-1 py-1 text-[11px] leading-4">
      <span className="sr-only">{t("review.targetSelection.matchTypes")}: </span>
      {labels.join(" · ") || "—"}
    </div>
  );
}
